import React, { FC, useEffect, useState } from "react";
import CustomTable from "../../../../components/Tables/CustomTable";
import { MoreVertical } from "lucide-react";
import DropdownMenu from "../../../ui/DropdownMenu";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../redux/app/hooks";

import { RootState } from "../../../../redux/app/store";
import moment from "moment";
import { cleanObject, formatNumberWithCommas } from "../../../../helpers";
import { getStatusStyles } from "../util";
import { fetchAllOrdersInTransitAsync } from "../../../../redux/features/orderSlice";
import UpdateDispatchStatus from "../UpdateDispatchStatus";
import ConfirmDispatch from "../ConfirmDispatch";

interface OrderInTransitProps {
  courierOfficeId: string | undefined;
  openBulkModal: boolean;
  cancelBulkModal: () => void;
  agentId: string | undefined;
}

const OrdersInTransitTable: FC<OrderInTransitProps> = ({
  courierOfficeId,
  openBulkModal,
  cancelBulkModal,
  agentId,
}) => {
  const { loading, ordersInTransit } = useAppSelector(
    (state: RootState) => state.orders
  );
  const [openUpdateDispatchModal, setOpenUpdateDispatchModal] = useState(false);
  const [executedOrderPageLimit, setExecutedOrderPageLimit] =
    useState<number>(10);
  const [executedOrderPage, setExecutedOrderPage] = useState<number>(0);
  const [orderToEdit, setOrderToEdit] = useState(null);
  const [orderIds, setOrderIds] = useState<string[]>([]);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleOrderCheck = (
    e: React.ChangeEvent<HTMLInputElement>,
    orderId: string
  ) => {
    const { checked } = e.target;
    setOrderIds((prev) => {
      const current = prev || [];
      if (checked) {
        return [...current, orderId];
      } else {
        return current.filter((id) => id !== orderId);
      }
    });
  };

  const orderInTransitColumns = [
    { label: "", field: "checkbox" },
    { label: "Contract", field: "contract_id" },
    { label: "Order Weight(Kg)", field: "order_weight" },
    { label: "Name of Vendor", field: "vendor_name" },
    { label: "Name of Buyer", field: "buyer_name" },
    { label: "Pickup Location", field: "pickup_location" },
    { label: "Contract Code", field: "contract_code" },
    { label: "Order Amount", field: "order_amount" },
    { label: "Actual Courier Fee", field: "actual_fee" },
    { label: "Estimated Courier Fee", field: "estimated_fee" },
    { label: "Order Date", field: "order_date" },
    { label: "Dispatch Status", field: "dispatch_status" },
    { label: "Order Status", field: "status" },
    // { label: "Courier Balance Status", field: "courier_status" },
    { label: "Action", field: "action" },
  ];

  const orderInTransitDropdownOptions = [
    {
      label: "View order details",
      onClick: (id: string) => navigate(`/order-details/${id}`),
      returnBy: "id",
    },
    // {
    //   label: "Confirm balance paid",
    //   onClick: (item: any) => {
    //     setOpenEditModal(true);
    //     setOrderToEdit(item);
    //   },
    //   returnAll: true,
    // },
    {
      label: "Update order location",
      onClick: (item: any) => {
        setOpenUpdateDispatchModal(true);
        setOrderToEdit(item);
      },
      returnAll: true,
    },
  ];

  const executedOrderOrderData = ordersInTransit?.data?.map(
    (order: any, index: number) => {
      const isPaid = order?.status === "PAID" || order?.status === "COMPLETED";
      return {
        id: index + 1,
        checkbox: (
          <input
            type="checkbox"
            className="w-4 h-4  text-black border-4 border-black"
            onChange={(e) => handleOrderCheck(e, order?.id)}
          />
        ),
        contract_id: order?.contract?.title,
        order_weight: order?.contract?.actualWeight ?? "N/A",
        vendor_name: order?.vendor?.fullName,
        buyer_name: order?.buyer?.fullName,
        pickup_location: order?.pickupLocation?.address,
        contract_code: order?.contract?.contractCode,
        order_date: moment(order?.createdAt).format("Do MMM YYYY"),
        order_amount: `₦${formatNumberWithCommas(order?.orderAmount)}`,
        dispute_reason: "Poor product quality",
        actual_fee: "₦" + formatNumberWithCommas(order?.actualCourierFee),
        estimated_fee: "₦" + formatNumberWithCommas(order?.estimatedCourierFee),
        courier_status: (
          <span
            className={`text-xs px-3 py-1 rounded-md font-semibold ${
              order?.courierFeePaymentStatus === "PAID" &&
              "bg-green-50 text-green-500 border border-green-500"
            }
               ${
                 order?.courierFeePaymentStatus === "PENDING" &&
                 "bg-yellow-50 text-yellow-500 border border-yellow-500"
               }`}
          >
            {order?.courierFeePaymentStatus}
          </span>
        ),
        status: (
          <span
            className={`text-xs px-3 py-1 rounded-md font-semibold ${
              isPaid
                ? "bg-green-50 text-green-500 border border-green-500"
                : order?.status === "PENDING"
                ? "bg-yellow-50 text-yellow-500 border border-yellow-500"
                : "bg-red-50 text-red-500 border border-red-500"
            }`}
          >
            {order?.status}
          </span>
        ),

        dispatch_status: (
          <span
            className={`text-xs px-3 py-1 rounded-md font-semibold ${getStatusStyles(
              order?.dispatchStatus
            )}`}
          >
            {order?.dispatchStatus}
          </span>
        ),
        action: (
          <DropdownMenu
            options={orderInTransitDropdownOptions}
            icon={
              <MoreVertical style={{ cursor: "pointer" }} className="z-0" />
            }
            item={order}
          />
        ),
      };
    }
  );

  useEffect(() => {
    const params = cleanObject({
      limit: executedOrderPageLimit,
      page: executedOrderPage + 1,
      courierOfficeId,
    });
    dispatch(fetchAllOrdersInTransitAsync(params));
  }, [dispatch, executedOrderPage, executedOrderPageLimit, courierOfficeId]);


  return (
    <>
      <CustomTable
        data={{
          columns: orderInTransitColumns,
          rows: executedOrderOrderData,
        }}
        noRecordText="No Order In Transit"
        loading={loading}
        showFooter={true}
        totalPages={ordersInTransit?.meta?.totalPages}
        itemsPerPage={ordersInTransit?.meta?.itemsPerPage}
        currentPage={ordersInTransit?.meta?.currentPage}
        onPageChange={(value: number) => setExecutedOrderPage(value)}
        onLimitChange={(value: number) => setExecutedOrderPageLimit(value)}
      />
      <UpdateDispatchStatus
        isOpen={openUpdateDispatchModal}
        onClose={() => setOpenUpdateDispatchModal(false)}
        order={orderToEdit}
        onSave={() => {
          const params = cleanObject({
            limit: executedOrderPageLimit,
            page: executedOrderPage + 1,
            courierOfficeId,
          });
          dispatch(fetchAllOrdersInTransitAsync(params));
        }}
      />

      <ConfirmDispatch
        isOpen={openBulkModal}
        dispatchDetails={{
          courierOfficeId: courierOfficeId,
          orderIds,
          dispatchStatus: "Arrived",
        }}
        onClose={() => {
          cancelBulkModal();
        }}
        onSave={() => {
          cancelBulkModal();
          const params = cleanObject({
            limit: executedOrderPageLimit,
            page: executedOrderPage + 1,
            courierOfficeId,
          });
          dispatch(fetchAllOrdersInTransitAsync(params));
        }}
        agentId={agentId}
      />
    </>
  );
};

export default OrdersInTransitTable;
